import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../services/supabaseClient'

// System administrators who have access to /admin
const SYSTEM_ADMINS = [
  'john@flowlogicllc.com'
]

interface AdminProtectedRouteProps {
  children: React.ReactNode
}

export function AdminProtectedRoute({ children }: AdminProtectedRouteProps) {
  const { user, isLoading } = useAuth()
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null)
  const [checkingAuth, setCheckingAuth] = useState(true)

  useEffect(() => {
    const checkAdminAccess = async () => {
      if (!user) {
        setIsAuthorized(false)
        setCheckingAuth(false)
        return
      }

      // First check hardcoded list
      if (SYSTEM_ADMINS.includes(user.email || '')) {
        setIsAuthorized(true)
        setCheckingAuth(false)
        return
      }

      // Then check database
      try {
        const { data, error } = await supabase
          .from('system_admins')
          .select('email')
          .eq('email', user.email)
          .single()

        if (data && !error) {
          setIsAuthorized(true)
        } else {
          setIsAuthorized(false)
        }
      } catch (error) {
        setIsAuthorized(false)
      }
      
      setCheckingAuth(false)
    }

    if (!isLoading) {
      checkAdminAccess()
    }
  }, [user, isLoading])

  // Show loading state while checking authentication
  if (isLoading || checkingAuth || isAuthorized === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    )
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Redirect to dashboard if not authorized
  if (!isAuthorized) {
    return <Navigate to="/dashboard" replace />
  }

  // User is authenticated and authorized
  return <>{children}</>
}